/**
 * Login Component
 * Login/Register/Forgot-password form with authentication
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import axios from 'axios';
import { API_URL, FORGOT_PASSWORD_TIMEOUT_MS, HELD_REQUEST_TIMEOUT_MS, OWN_CREDENTIAL, SEND_ONCE } from './config';
import LiangFanLogo from './components/ui/LiangFanLogo';
// Bundle de-bloat (audit r3 discipline): Login is App.js's ONE static
// (non-lazy) route import, so it's in the EAGER entry graph — Spinner is
// imported by its DIRECT path here (matching App.js itself), not the
// `./components/ui` barrel, so no other barrel member rides along into the
// entry chunk.
import Spinner from './components/ui/Spinner';
import PendingStatus from './components/ui/PendingStatus';
import busyFormProps from './utils/busyFormProps';
import isVisitorsOwnRefusal, { visitorRefusalFallback } from './utils/visitorRefusal';
// Already in the entry graph (AgeConsentGate → AgeGateModal imports it).
import { busyButton } from './components/ui/busyForm';
import './styles/liang-brand.css';
import './Login.css';

// What the form says when a write got no answer at all (a dropped connection,
// the deadline, or a 200 that can't sign anyone in). It may have done its
// work, so neither says "try again": see the catch in handleSubmit. Register's
// names the username "Log in with these details" fills in (after a changed
// username, the first one), and says what to do if logging in fails.
const UNCONFIRMED_MESSAGES = {
  register: (username) => `We couldn't confirm your account was created. Try logging in as “${username}” with the password you just entered before registering again. If that doesn't work, the username may belong to someone else — choose another.`,
  forgot: () => "We couldn't confirm the reset email was sent. Check your inbox and spam folder in a minute before asking again — a new request replaces any earlier link.",
};

function Login({ onLogin }) {
  // Guardian-invite context (2026-10-10): the student→guardian invite email
  // links to `/guardian/accept#token=…`. App.js's logged-out catch-all renders
  // THIS component at that URL (fragment intact), and the email tells the
  // guardian to "create a guardian account and open this invite link". So when
  // we're sitting on the accept path, open on the Register tab with the
  // guardian role preselected and say why — the account they need is one click
  // away instead of a hunt through a Student/Teacher toggle that didn't offer
  // it. After they register or log in, App.js re-matches the SAME location and
  // GuardianAcceptPage consumes the token. We only READ the pathname here —
  // the fragment is never touched, so it survives into the authenticated app.
  const location = useLocation();
  const isGuardianInvite = location.pathname === '/guardian/accept';
  const defaultRole = isGuardianInvite ? 'guardian' : 'student';
  // ResetPassword's "Request a new reset link" (for a link that can't be used,
  // or a reset that got no answer) goes to /forgot-password, which App.js's
  // logged-out catch-all also renders here: open on the reset-link request.
  const opensOnForgot = location.pathname === '/forgot-password';

  // view: 'login' | 'register' | 'forgot' | 'forgot-sent'
  const [view, setView] = useState(isGuardianInvite ? 'register' : opensOnForgot ? 'forgot' : 'login');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    // POST-AIQ-119 (2026-05-17): teacher signup is gated on a shared
    // out-of-band invite code (backend/app/routes/auth.py:295-309 from
    // SEC-001). Without this field the form couldn't submit the code
    // even when the operator had set TEACHER_INVITE_CODE on the
    // backend — so teacher signup was effectively dead-disabled.
    teacherInviteCode: '',
    // POST-AIQ-151 COPPA-PR1 (2026-06-01): date of birth, required by the
    // backend for student registrations (the durable COPPA age signal).
    // NEUTRAL by design — a plain "Date of birth" field with NO "must be
    // 13+" hint, which would teach the self-attestation workaround. The
    // richer age flows (age_required post-login prompt, under-13 limited
    // mode) are PR4; this field just keeps NEW student signups working now
    // that DOB is required.
    dateOfBirth: '',
  });
  // role: 'student' | 'teacher' | 'guardian' — the three self-serve roles the
  // backend accepts (routes/auth.py `req.role not in ("student", "teacher",
  // "guardian")`). Guardian needs neither the teacher invite code nor a date of
  // birth: a fresh guardian can't see any minor's data until a student links
  // to them and consent is granted (both server-enforced), so self-signup
  // carries no privilege on its own.
  const [role, setRole] = useState(defaultRole);
  // { message, calm } | null. `calm` for a refusal about what the visitor sent
  // or did (utils/visitorRefusal), shown in the amber notice look; red for a
  // fault of the system's.
  const [error, setError] = useState(null);
  // 'register' | 'forgot' | null: the view whose write got no answer at all
  // (UNCONFIRMED_MESSAGES). Kept apart from `error`, a refusal or a fault.
  const [unconfirmed, setUnconfirmed] = useState(null);
  const [loading, setLoading] = useState(false);
  // The register that got no answer in this visit, as typed: the account it
  // may have made. While its notice is up, a move to Login keeps its username
  // and password, and a later "already exists" for its username or email
  // brings the notice back instead of a red dead end. A login refused for
  // exactly these details proves the account isn't theirs, and clears it
  // (handleSubmit), so a username that belongs to someone else can't loop.
  // Component state only: never stored, never in a URL.
  const [mayExist, setMayExist] = useState(null);

  // A11y (2026-07-13): move focus to the new view's heading on every in-page
  // view swap (login ↔ register ↔ forgot ↔ forgot-sent), so a keyboard/SR user
  // isn't stranded on the now-unmounted control that triggered the swap (WCAG
  // 2.4.3). `headingRef` is attached to whichever element is the current view's
  // focus target — the active tab for login/register (a real focusable button),
  // or the level-2 heading for forgot/forgot-sent (made programmatically
  // focusable via tabIndex={-1}). Only ONE of those is mounted per view, so a
  // single ref is unambiguous.
  const headingRef = useRef(null);
  // Focus moves only when the view has actually changed: on initial page load
  // it would steal focus from the top of the document (and the skip-nav
  // target). Comparing against the view focus was last moved for, rather than
  // a "first run" flag, also holds when the effect is replayed (StrictMode).
  //
  // A LAYOUT effect, so focus moves in the commit that swaps the view. That
  // commit can remove the control that has focus: the "Forgot password?" link,
  // or the field or button that held it while the reset email was requested.
  // On a later timer the visitor sat on <body> in between.
  const focusedViewRef = useRef(view);
  useLayoutEffect(() => {
    if (focusedViewRef.current === view) return;
    focusedViewRef.current = view;
    headingRef.current?.focus();
  }, [view]);

  // Except an arrival by a link inside the app (ResetPassword's and
  // VerifyEmail's pass utils/focusOnArrival): the link that had focus went
  // with the page it was on, so the opening view's focus target takes it, as
  // after a swap. A cold load or a typed URL carries no state, and moves no
  // focus.
  const arrivedByLinkRef = useRef(Boolean(location.state?.focusView));
  useLayoutEffect(() => {
    if (arrivedByLinkRef.current) headingRef.current?.focus();
  }, []);

  // A register that got no answer may have made the account, and its next
  // step, logging in with what was just typed, is not the control the visitor
  // pressed. So the message saying so takes focus (as Classrooms' unconfirmed
  // create does), with that step the next Tab stop. A reset-link request's
  // next step is the inbox, or this same button later: focus stays put there.
  const noticeRef = useRef(null);
  useLayoutEffect(() => {
    if (unconfirmed === 'register') noticeRef.current?.focus();
  }, [unconfirmed]);
  // The notice's box (its message and its button) and the submit button: a
  // submit takes the notice away, so focus inside it moves to the button
  // first (handleSubmit).
  const noticeBoxRef = useRef(null);
  const submitRef = useRef(null);
  // The field only the current role has (Teacher Invite Code, Date of birth):
  // one at a time, so one ref. A role change takes it away (chooseRole).
  const roleFieldRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
    setUnconfirmed(null);
  };

  // Reached only through busyFormProps (below), which has already cancelled
  // the submit event and refused it if a request is still running.
  //
  // Every request here has a deadline (HELD_REQUEST_TIMEOUT_MS per attempt;
  // the reset-link request's is FORGOT_PASSWORD_TIMEOUT_MS, and config.js
  // says why). Busy ends only when the request settles, so a stalled
  // connection used to hold the form, taking no typing, refusing every submit
  // and reading "Logging In...", until a reload. Past the deadline axios fails
  // with no `response`, as for a dropped connection, and the form works again.
  // What it says then depends on the request: see the catch below.
  //
  // Whether App.js's retry policy may send one again by itself (up to three
  // more times, 250 ms to 1 s apart) after a dropped connection, which can
  // come AFTER the server did the work, or a 408 / 429 / 502 / 503 / 504:
  //   - login: yes. A repeat signs in again (one more refresh-token family;
  //     the one whose answer was lost goes unused and expires) or is refused
  //     again, so nothing the visitor is then shown is wrong. It does cost
  //     rate-limit slots: every attempt that reaches the server counts
  //     against the auth limit (AUTH_RATE_LIMIT, 5 a minute per IP and
  //     route), and a backend that crashes on each login answers a 500 with
  //     no CORS headers, which reads here as a dropped connection. One press
  //     then spends 4 slots, and the next reads "Too many requests. Please
  //     slow down." (The policy ignores Retry-After and re-sends a POST after
  //     a drop; changing that is App.js's fix, not this form's.)
  //   - register and forgot-password: no, they go out once (SEND_ONCE),
  //     whatever that policy is. A register sent again comes home "already
  //     exists" for the account it had just made. A reset-link request sent
  //     again mints a new link, which voids the one just emailed, and sends a
  //     second email. SEND_ONCE switches off the whole policy, not only the
  //     re-send after a drop: a 503 while the backend redeploys, which a
  //     re-send used to ride out, now shows on the first press.
  //
  // None of the three carries the stored session (OWN_CREDENTIAL): a
  // sign-in's 401 is a wrong password, and a reset-link request carries
  // nothing, so no session refresh and no sign-out follows one. With a session
  // another tab had stored, a wrong password used to refresh that session and
  // send the sign-in again, spending another rate-limit slot and rotating the
  // session for nothing.
  const handleSubmit = async () => {
    // A press of the submit button that moves no focus leaves focus where it
    // was: on the no-answer notice, if that had it, which this submit takes
    // away. Such a press is a screen reader's or voice control's activation, a
    // scripted click(), or reportedly a tap in Safari on an iPad or iPhone. (A
    // mouse click in desktop Safari or Firefox on macOS most likely moves focus
    // to <body> on mousedown, before this runs; nothing here causes or changes
    // that.) Hand focus to the button first, as a press that focused it would
    // have (it stays aria-disabled, never disabled, while the request runs),
    // or it falls to <body> for the whole request and after it.
    if (noticeBoxRef.current?.contains(document.activeElement)) {
      submitRef.current?.focus();
    }
    setLoading(true);
    setError(null);
    setUnconfirmed(null);

    try {
      if (view === 'forgot') {
        await axios.post(
          `${API_URL}/api/v1/auth/forgot-password`,
          { email: formData.email },
          { timeout: FORGOT_PASSWORD_TIMEOUT_MS, ...SEND_ONCE, ...OWN_CREDENTIAL },
        );
        setView('forgot-sent');
        return;
      }

      const endpoint = view === 'register' ? '/api/v1/auth/register' : '/api/v1/auth/login';
      const payload = view === 'register'
        ? {
            username: formData.username,
            email: formData.email,
            password: formData.password,
            full_name: formData.fullName,
            role: role,
            // POST-AIQ-119: only sent for teacher registration.
            // The backend ignores it for students.
            ...(role === 'teacher'
              ? { teacher_invite_code: formData.teacherInviteCode }
              : {}),
            // POST-AIQ-151 COPPA-PR1: date_of_birth is required by the
            // backend for student registrations and optional/ignored for
            // teachers. Send it only on the student path (mirrors the
            // conditional spread for the teacher invite code).
            ...(role === 'student'
              ? { date_of_birth: formData.dateOfBirth }
              : {}),
          }
        : {
            username: formData.username,
            password: formData.password,
          };

      const response = await axios.post(
        `${API_URL}${endpoint}`,
        payload,
        { timeout: HELD_REQUEST_TIMEOUT_MS, ...(view === 'register' ? SEND_ONCE : {}), ...OWN_CREDENTIAL },
      );
      const { access_token, refresh_token, user } = response.data;
      // POST-AIQ-059 (2026-05-13): shape guard against a malformed
      // auth response. Without this, a BE contract drift (e.g.
      // returning `accessToken` instead of `access_token`) destructures
      // `access_token` to `undefined` and the line below stores the
      // literal string `"undefined"` in localStorage. Every subsequent
      // request then sends `Authorization: Bearer undefined`, gets a
      // 401, and the student is permanently locked out with a
      // confusing "Invalid username or password" on next login. The
      // throw lands in the catch block below, which treats it as an
      // answer that never came ("Connection error. Please try again."
      // on Login; on Register, where the account was made, the notice
      // that says so) AND leaves a console.error breadcrumb for Sentry.
      if (
        typeof access_token !== 'string'
        || !access_token
        || !user
        || typeof user !== 'object'
      ) {
        throw new Error('Malformed auth response from server');
      }
      // POST-AIQ-042 Phase 2 (2026-05-11): persist BOTH tokens. The
      // refresh token feeds the global axios interceptor's silent-rotation
      // path so a 60-min access TTL is invisible to the student.
      // Phase 1 (PR #164) silently dropped `refresh_token` here — that's
      // the bug this PR fixes.
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      localStorage.setItem('user', JSON.stringify(user));
      onLogin(access_token, user, view === 'register');
    } catch (err) {
      console.error('Authentication error:', err);
      const status = err.response?.status;
      if (!err.response && (view === 'register' || view === 'forgot')) {
        // No answer at all: a dropped connection, the deadline, or a 200
        // that signs no one in (the throw above). The write may have done
        // its work, so "try again" is the wrong advice: a second register
        // comes home "already exists" for the account the first one made,
        // and a second reset-link request voids the link the first one
        // emailed. Say what is known, and the safe next step. (`finally`
        // still runs after this return.)
        if (view === 'register') {
          setMayExist({
            username: formData.username,
            email: formData.email,
            password: formData.password,
          });
        }
        setUnconfirmed(view);
        return;
      }
      if (
        view === 'register' && status === 409 && mayExist
        && (mayExist.username === formData.username
          || mayExist.email.toLowerCase() === formData.email.toLowerCase())
      ) {
        // "Already exists" for the username or email of the register that
        // got no answer earlier in this visit: most likely the account it
        // made. The same notice and way on, not a red dead end.
        setUnconfirmed('register');
        return;
      }
      if (
        view === 'login' && status === 401 && mayExist
        && formData.username === mayExist.username
        && formData.password === mayExist.password
      ) {
        // Refused for exactly the details of that register: it never made
        // the account (most often it never reached the server), and the
        // username may be someone else's. The match ends here, so the next
        // "already exists" is the server's own, saying what to change.
        setMayExist(null);
      }
      const show = (message) => setError({ message, calm: isVisitorsOwnRefusal(status) });
      // POST-AIQ-118 (2026-05-17): the backend returns errors in TWO
      // shapes depending on which exception path fires:
      //   (a) ValidationException → AppException handler →
      //       `{"error": {"code": ..., "message": "...", "details": {...}}}`
      //       — used by create_user's password-complexity checks
      //       ("Password must contain at least one uppercase letter",
      //       "...lowercase...", "...digit...", min length, max length).
      //   (b) raw HTTPException(detail=...) → FastAPI default handler →
      //       `{"detail": "..."}`
      //       — used by route-level guards (e.g. teacher invite code,
      //       malformed JSON body, role-not-student-or-teacher).
      //   (c) Pydantic body-validation failures → 422 with structured
      //       errors array.
      //
      // Previously this handler only read (b), so the new ValidationException
      // shape (a) fell into the generic 400 fallback "Username or email
      // already exists" — a wrong, misleading message that sent users into
      // an infinite retry loop. Worse: a user who typed a password
      // missing an uppercase letter would see "Username or email already
      // exists" and infinitely retry usernames.
      const data = err.response?.data;
      // Bug-hunt P1 (UX sweep 2026-07-12): `error.message` MUST be a STRING
      // before we trust it as a ready-to-render message. A known backend
      // contract bug (filed separately — NOT fixed here) returns
      // `error.message` as the RAW pydantic validation-errors ARRAY
      // (`[{type, loc, msg, input}, ...]`) instead of a string on some 422s.
      // Without this guard, `setError(structuredMessage)` stored the array
      // directly in state and `{error}` in the JSX below threw "Objects are
      // not valid as a React child" during render. Login mounts OUTSIDE any
      // ErrorBoundary for logged-out visitors (App.js), so that render throw
      // used to unmount the ENTIRE app to a permanent blank screen. Rejecting
      // non-strings here lets the array case fall through to
      // `pydanticFirstMsg` below, which now also unwraps that exact shape.
      const structuredMessage =
        typeof data?.error?.message === 'string' ? data.error.message : null;
      const legacyDetail = typeof data?.detail === 'string' ? data.detail : null;
      // 422 from FastAPI's RequestValidationError: surface the first error's
      // message if it's a list of {msg, loc} objects (our errors.py wraps
      // this in `error.details.errors`). The third clause handles the
      // backend-contract-bug shape from the guard above: `error.message`
      // ITSELF is the raw pydantic errors array (not nested under
      // `.details.errors`), read the same way FastAPI's own default
      // `detail` array shape is read (`[0].msg`).
      //
      // Bug-hunt (2026-07-12 hardening pass): both `.msg` reads used to trust
      // `.msg` was always a string. A hostile/malformed 422 with `.msg` an
      // object flowed into `setError(...)`, and `{error}` in the JSX below
      // threw "Objects are not valid as a React child" during render (the
      // same crash class the `structuredMessage` guard above fixes).
      // `String(...)` coerces any non-null `.msg` to a renderable primitive;
      // worst case is an ugly-but-harmless "[object Object]", never a
      // blank-screen crash.
      const detailMsg = Array.isArray(data?.detail) ? data.detail[0]?.msg : null;
      const errorArrayMsg = Array.isArray(data?.error?.message)
        ? data.error.message[0]?.msg
        : null;
      const pydanticFirstMsg =
        data?.error?.details?.errors?.[0]?.message ||
        (detailMsg != null ? String(detailMsg) : null) ||
        (errorArrayMsg != null ? String(errorArrayMsg) : null);
      if (structuredMessage) {
        show(structuredMessage);
      } else if (legacyDetail) {
        show(legacyDetail);
      } else if (pydanticFirstMsg) {
        show(pydanticFirstMsg);
      } else if (status === 401) {
        show('Invalid username or password');
      } else if (status === 409) {
        // Future-proof: when the backend distinguishes "already exists"
        // (HTTP 409 Conflict, conventional) from "validation failure"
        // (HTTP 400), the conflict message lives here.
        show('Username or email already exists');
      } else if (status === 400 && view === 'register') {
        // Last-resort 400 fallback for register. The structured shape SHOULD
        // have caught the real message above; this only fires when the body
        // is empty or malformed. Generic but honest — does NOT claim
        // "already exists" (the misleading message that caused this bug).
        show('Could not register. Check that your password has at least 8 characters, one uppercase letter, one lowercase letter, and one digit.');
      } else if (status === 400) {
        show('Bad request. Please check your input and try again.');
      } else if (isVisitorsOwnRefusal(status)) {
        // A refusal with no message to show: calm words for the calm look.
        show(visitorRefusalFallback(status));
      } else {
        show('Connection error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const switchView = (next) => {
    // Never under a running request. Its handler answers for the view that
    // sent it, so a swap now would put that answer ("Invalid username or
    // password", a sign-in) on a form the visitor had moved to. Every control
    // that calls this is refused meanwhile by busyButton (see them, below);
    // this is the backstop for one that isn't.
    if (loading) return;
    // While the notice for a register that got no answer is up, any move to
    // Login (its own button, the tab, "Login here") keeps that register's
    // username and password, to sign in to the account it may have made.
    // Every other swap starts the new view empty.
    const kept = next === 'login' && unconfirmed === 'register' ? mayExist : null;
    setView(next);
    setError(null);
    setUnconfirmed(null);
    setRole(defaultRole);
    setFormData({
      username: kept ? kept.username : '',
      email: '',
      password: kept ? kept.password : '',
      fullName: '',
      teacherInviteCode: '',
      dateOfBirth: '',
    });
  };

  // A role change removes the old role's own field. A press of the role button
  // that moves no focus (a screen reader's or voice control's activation, a
  // scripted click(), reportedly a tap in Safari on an iPad or iPhone) leaves
  // focus in that field if it had it, and with the field gone, on <body>. So
  // focus moves to the pressed button first, as a press that focused it would
  // have. A press that did focus it, or one made from a field the change
  // keeps, moves none. (A mouse click in desktop Safari or Firefox on macOS
  // most likely moves focus to <body> on mousedown, before this runs: the
  // field has lost it already, and nothing here causes or changes that.)
  const chooseRole = (event, next) => {
    if (next !== role && roleFieldRef.current?.contains(document.activeElement)) {
      event.currentTarget.focus();
    }
    setRole(next);
  };

  // A browser drops focus to <body> from a focused control that becomes
  // `disabled`, and nothing brought it back after a wrong password. So while a
  // request runs, no field and no submit button here is natively disabled:
  // whichever one the visitor pressed keeps their focus, is announced as
  // unavailable, and works again if the request fails. busyFormProps is that
  // contract, including the guard that refuses a second request.
  const form = busyFormProps({ busy: loading, onSubmit: handleSubmit });

  const isLoginOrRegister = view === 'login' || view === 'register';

  return (
    <main className="login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="liang-splash">
            <div className="liang-splash__logo-wrap" aria-hidden="true">
              <LiangFanLogo variant="full" size={132} title="" />
            </div>
            <span className="liang-splash__tagline">Learning Companion</span>
          </div>
          <h1 className="liang-wordmark liang-wordmark--login">Liang</h1>
          <p className="tagline">Learn through guided discovery</p>
        </div>

        {isLoginOrRegister && (
          // While a request runs, the tabs are refused (busyButton) and say so
          // with aria-disabled, never native `disabled`: a tab can be holding
          // focus as a request starts. A view swap puts focus on the tab, a
          // password manager can fill the fields without taking it, and a
          // press of the submit button that moves no focus (a screen reader's
          // or voice control's activation, a scripted click()) leaves it
          // there. A focused control that becomes `disabled` drops focus to
          // <body>.
          <div className="login-tabs" role="tablist" aria-label="Log in or create an account">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'login'}
              ref={view === 'login' ? headingRef : undefined}
              className={view === 'login' ? 'active' : ''}
              {...busyButton({ busy: loading, onClick: () => view !== 'login' && switchView('login') })}
            >
              Login
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'register'}
              ref={view === 'register' ? headingRef : undefined}
              className={view === 'register' ? 'active' : ''}
              {...busyButton({ busy: loading, onClick: () => view !== 'register' && switchView('register') })}
            >
              Register
            </button>
          </div>
        )}

        {view === 'forgot-sent' ? (
          <div className="forgot-sent" role="status">
            <div className="success-icon" aria-hidden="true">✓</div>
            {/* It takes focus, so the line under it is its description: a
                screen reader says both as focus arrives. */}
            <h2 ref={headingRef} tabIndex={-1} aria-describedby="forgot-sent-text">Check your email</h2>
            <p id="forgot-sent-text">If that email is registered, we've sent a password reset link. It expires in 1 hour.</p>
            <button className="link-button" onClick={() => switchView('login')}>Back to login</button>
          </div>
        ) : (
          // Nothing on this form is natively disabled while a request runs.
          // The fields and the submit button follow busyFormProps; the role
          // toggles, "Forgot password?", "Back to login" and the footer's
          // "Login here" / "Register here" are refused by busyButton, as the
          // tabs are. Any of them can be holding focus as a request starts: a
          // press of the submit button that moves no focus (a screen reader's
          // or voice control's activation, a scripted click()) leaves it
          // wherever the visitor Tabbed to, and a focused control that
          // becomes `disabled` drops focus to <body>.
          <form {...form.formProps} className="login-form">
            {view === 'forgot' && (
              <div className="forgot-header">
                <h2 ref={headingRef} tabIndex={-1}>Reset your password</h2>
                <p>Enter your email and we'll send you a reset link.</p>
              </div>
            )}

            {/* A refusal about what the visitor sent or did is their own
                situation, in the calm look; red is for the system's faults. */}
            {error && (
              <div className={error.calm ? 'login-notice' : 'error-message'} role="alert">
                {error.message}
              </div>
            )}

            {/* An answer that never came is the visitor's situation, not a
                fault: the calm look, never the red one. The live region holds
                the message alone; the next step follows it. An alert's name
                comes only from its author, so the message is labelled by
                itself: focus arriving on it reads the message (Classrooms'
                unconfirmed notice does the same). */}
            {unconfirmed && (
              <div className="login-notice" ref={noticeBoxRef}>
                <p
                  id="login-notice-message"
                  className="login-notice__message"
                  role="alert"
                  aria-labelledby="login-notice-message"
                  tabIndex={-1}
                  ref={noticeRef}
                >
                  {UNCONFIRMED_MESSAGES[unconfirmed](mayExist?.username ?? formData.username)}
                </p>
                {unconfirmed === 'register' && (
                  <button
                    type="button"
                    className="link-button login-notice__action"
                    onClick={() => switchView('login')}
                  >
                    Log in with these details
                  </button>
                )}
              </div>
            )}

            {/* Guardian-invite context: say why this screen opened on Register
                with the guardian role chosen, and that an existing guardian can
                just log in — the invite is accepted right after either. */}
            {isGuardianInvite && isLoginOrRegister && (
              <div className="login-invite-note" role="status">
                <strong>You’ve been invited to support a student.</strong>{' '}
                {view === 'register'
                  ? 'Create a parent or guardian account below — we’ll accept the invite as soon as you’re in.'
                  : 'Log in to your guardian account — we’ll accept the invite as soon as you’re in.'}
              </div>
            )}

            {view !== 'forgot' && (
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input type="text" id="username" name="username" autoComplete="username" value={formData.username}
                  onChange={handleChange} required {...form.fieldProps} placeholder="Enter your username" />
              </div>
            )}

            {view === 'register' && (
              <>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" autoComplete="email" value={formData.email}
                    onChange={handleChange} required {...form.fieldProps} placeholder="your.email@example.com" />
                </div>
                <div className="form-group">
                  <label htmlFor="fullName">Full Name (optional)</label>
                  <input type="text" id="fullName" name="fullName" autoComplete="name" value={formData.fullName}
                    onChange={handleChange} {...form.fieldProps} placeholder="Your Name" />
                </div>
                <div className="form-group">
                  {/* A11y: a bare <label> with no associated control is inert to
                      AT. This is a labelled GROUP of toggle buttons, so the
                      visible label names a role="group" via aria-labelledby, and
                      each button reports its state with aria-pressed. */}
                  <span className="form-group__label" id="role-toggle-label">I am a...</span>
                  <div className="role-toggle" role="group" aria-labelledby="role-toggle-label">
                    <button
                      type="button"
                      className={role === 'student' ? 'active' : ''}
                      aria-pressed={role === 'student'}
                      {...busyButton({ busy: loading, onClick: (event) => chooseRole(event, 'student') })}
                    >
                      I'm a Student
                    </button>
                    <button
                      type="button"
                      className={role === 'teacher' ? 'active' : ''}
                      aria-pressed={role === 'teacher'}
                      {...busyButton({ busy: loading, onClick: (event) => chooseRole(event, 'teacher') })}
                    >
                      I'm a Teacher
                    </button>
                    <button
                      type="button"
                      className={role === 'guardian' ? 'active' : ''}
                      aria-pressed={role === 'guardian'}
                      {...busyButton({ busy: loading, onClick: (event) => chooseRole(event, 'guardian') })}
                    >
                      I'm a Parent or Guardian
                    </button>
                  </div>
                  {role === 'guardian' && (
                    <small className="password-hint">
                      For a parent or guardian supporting a student. No code
                      needed — once your account exists, open the invite link
                      from your student's email, or link to them from your
                      Family page.
                    </small>
                  )}
                </div>
                {/* POST-AIQ-119 (2026-05-17): Teacher invite code field.
                    Only rendered when role === 'teacher'. Submitted as
                    `teacher_invite_code` in the register payload (see
                    the conditional spread in the handler above). The
                    backend (routes/auth.py:295-309) requires this to
                    match TEACHER_INVITE_CODE env var or rejects with
                    403. SEC-001 invariant: prevents self-serve teacher
                    accounts that could read student PII via classroom
                    endpoints. The code itself is shared out-of-band
                    (Slack/email/etc.) to vetted teachers — never paste
                    it publicly. */}
                {role === 'teacher' && (
                  <div className="form-group" ref={roleFieldRef}>
                    <label htmlFor="teacherInviteCode">
                      Teacher Invite Code
                    </label>
                    <input
                      type="text"
                      id="teacherInviteCode"
                      name="teacherInviteCode"
                      value={formData.teacherInviteCode}
                      onChange={handleChange}
                      required
                      {...form.fieldProps}
                      placeholder="Provided by your administrator"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                    />
                    <small className="password-hint">
                      Required for teacher accounts. Contact your
                      administrator if you don't have one.
                    </small>
                  </div>
                )}
                {/* POST-AIQ-151 COPPA-PR1 (2026-06-01): date-of-birth field
                    for student registration. The backend requires
                    date_of_birth for role='student' (the durable COPPA age
                    signal) and ignores it for teachers, so this renders only
                    on the student path. NEUTRAL copy by design — a plain
                    "Date of birth" label with NO "you must be 13+" hint,
                    which would teach a child the self-attestation workaround.
                    A native date input gives keyboard + screen-reader
                    support and a platform date picker for free. The richer
                    age-gate flows (age_required prompt, under-13 limited
                    mode) are PR4.
                    autoComplete="off", unlike the fields around it. "bday"
                    (WCAG 1.3.5's token for this) asks a browser or password
                    manager to offer a stored birthday: on a shared family
                    device that is the profile owner's, an adult's, and a child
                    who accepts it registers as an adult, which decides the
                    COPPA consent path. Leaving the token out is not a request
                    (some fillers guess from the name and label), so this says
                    "off". A deliberate 1.3.5 exception, recorded for the
                    owner; coppa/AgeGateModal.js asks the same. */}
                {role === 'student' && (
                  <div className="form-group" ref={roleFieldRef}>
                    <label htmlFor="dateOfBirth">Date of birth</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      autoComplete="off"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                      {...form.fieldProps}
                    />
                  </div>
                )}
              </>
            )}

            {view === 'forgot' && (
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input type="email" id="email" name="email" autoComplete="email" value={formData.email}
                  onChange={handleChange} required {...form.fieldProps} placeholder="your.email@example.com" />
              </div>
            )}

            {view !== 'forgot' && (
              <div className="form-group">
                <label htmlFor="password">Password</label>
                {/* One element serves both views, so its token follows the
                    view: Register asks a password manager for a new password
                    (to generate and save), Login for the saved one. */}
                <input type="password" id="password" name="password"
                  autoComplete={view === 'register' ? 'new-password' : 'current-password'}
                  value={formData.password}
                  onChange={handleChange} required {...form.fieldProps} placeholder="Enter your password" minLength={8} />
                {view === 'register' && (
                  /* POST-AIQ-118 (2026-05-17): the prior hint said only
                     "At least 8 characters" but `create_user` in
                     backend/app/auth.py also enforces uppercase + lowercase
                     + digit complexity. Users were typing 8+ char passwords
                     missing one of those (e.g. "passwordpassword") and
                     getting back the wrong error message — see the error
                     handler comment above. Disclose all four requirements
                     up-front so users don't trip the validator. */
                  <small className="password-hint">
                    8+ characters, with one uppercase, one lowercase, and one digit
                  </small>
                )}
                {view === 'login' && (
                  <button
                    type="button"
                    className="link-button forgot-link"
                    {...busyButton({ busy: loading, onClick: () => switchView('forgot') })}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            {/* Task #60 (2026-06-13): consent line at the sign-up point. Sits
                directly above the submit button so the agreement is presented
                at the moment of account creation. The privacy policy is a
                PUBLIC route, reachable without logging in. */}
            {view === 'register' && (
              <p className="consent-note">
                By creating an account you agree to our{' '}
                <Link to="/privacy" className="link-button consent-note__link">
                  Privacy Policy
                </Link>.
              </p>
            )}

            <button {...form.submitProps} ref={submitRef} className="submit-button">
              {loading ? (
                <><Spinner size="sm" decorative />
                  {view === 'register' ? 'Creating Account...' : view === 'forgot' ? 'Sending...' : 'Logging In...'}
                </>
              ) : (
                view === 'register' ? 'Create Account' : view === 'forgot' ? 'Send Reset Link' : 'Login'
              )}
            </button>
            {/* Decorative in-button spinner (aria-hidden) → sibling live region
                announces the pending state to screen readers. */}
            <PendingStatus pending={loading}>
              {view === 'register' ? 'Creating Account…' : view === 'forgot' ? 'Sending…' : 'Logging In…'}
            </PendingStatus>

            {view === 'forgot' && (
              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  className="link-button"
                  {...busyButton({ busy: loading, onClick: () => switchView('login') })}
                >
                  Back to login
                </button>
              </div>
            )}
          </form>
        )}

        {isLoginOrRegister && (
          <div className="login-footer">
            <p>
              {view === 'register' ? 'Already have an account? ' : "Don't have an account? "}
              <button
                className="link-button"
                {...busyButton({ busy: loading, onClick: () => switchView(view === 'register' ? 'login' : 'register') })}
              >
                {view === 'register' ? 'Login here' : 'Register here'}
              </button>
            </p>
          </div>
        )}

        {/* Task #60 (2026-06-13): minimal accessible footer link to the public
            privacy policy. Logged-out visitors have no app chrome / footer, so
            this is the discovery point for the policy on the auth screens (it
            renders for login, register, AND the forgot-password views). */}
        {/* A11y (2026-07-13): this <footer> is now scoped INSIDE the page
            <main>, so an explicit role="contentinfo" would be an invalid nested
            landmark. A scoped <footer> (not a direct child of <body>) carries no
            landmark role by default, which is exactly what we want here. */}
        <footer className="login-legal">
          <Link to="/privacy" className="login-legal__link">Privacy Policy</Link>
        </footer>
      </div>

      <div className="features-preview">
        <h2>What's Inside:</h2>
        <ul>
          <li>Adaptive hints that match your level</li>
          <li>Track your progress over time</li>
          <li>AI-generated learning insights</li>
          <li>Build study streaks</li>
          <li>Personalized recommendations</li>
        </ul>
      </div>
    </main>
  );
}

export default Login;
